import type { RedirectPaymentAction } from '@/api/types';

/**
 * Handing the browser to a hosted payment page.
 *
 * Two ways in, because gateways differ: some take a plain navigation, and GlobalPay's HPP is entered by
 * POSTing a form of fields. Both come from the server as one `redirect` action, so nothing here knows which
 * processor it is talking to.
 */

/**
 * Whether a URL is one we are willing to hand a guest to.
 *
 * The URL comes from our own API, so this is defence in depth rather than distrust. It is cheap, and the
 * thing it prevents is severe: a `javascript:` URL reaching `location.assign` is script execution on this
 * origin, which on a payment screen is the worst possible place to have it. A bug or a compromise upstream
 * should not become code execution down here.
 *
 * Plain HTTP is allowed only on loopback, for a gateway sandbox running locally. Anywhere else, sending a
 * guest to a payment page over HTTP is not a thing to be tolerant about.
 */
export function isSafeGatewayUrl(url: string): boolean {
    let parsed: URL;

    try {
        parsed = new URL(url);
    } catch {
        return false;
    }

    if (parsed.protocol === 'https:') return true;

    return (
        parsed.protocol === 'http:' &&
        (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1' || parsed.hostname === '[::1]')
    );
}

/**
 * Builds the form that enters a hosted page by POST.
 *
 * Separate from submitting it so the shape can be tested: jsdom does not implement form submission, and the
 * part worth checking is that every field the gateway expects arrives, named correctly and unmangled.
 *
 * Values go in as `value` on an input rather than into markup, so a field containing quotes or angle brackets
 * is carried literally instead of being parsed as HTML.
 */
export function buildRedirectForm(action: RedirectPaymentAction, doc: Document = document): HTMLFormElement {
    const form = doc.createElement('form');
    form.method = 'POST';
    form.action = action.url;
    form.hidden = true;

    for (const [name, value] of Object.entries(action.fields)) {
        const input = doc.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
    }

    return form;
}

/**
 * Sends the browser to the gateway. Does not return in practice — the page is being replaced.
 *
 * Returns false when the URL was refused, so a caller can show a failure rather than sit on a screen that
 * looks like it is still working.
 */
export function performRedirect(action: RedirectPaymentAction, target: Window = window): boolean {
    if (!isSafeGatewayUrl(action.url)) return false;

    if (action.httpMethod === 'GET') {
        target.location.assign(action.url);
        return true;
    }

    const doc = target.document;
    const form = buildRedirectForm(action, doc);

    doc.body.append(form);
    form.submit();

    return true;
}
